import { useEffect, useMemo, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useVatRegister } from "api/accountingService";

interface Props {
  searchText: string;
  setSubtitle: (s: string) => void;
}

const CURRENT_YEAR = new Date().getFullYear();
const todayISO = () => new Date().toISOString().slice(0, 10);

const fmt = (v: number): string =>
  v || v === 0
    ? v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "";

const plDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString("pl-PL") : "");

const TYPE_LABEL: Record<string, string> = {
  Purchase: "Zakup (VAT naliczony)",
  Sale: "Sprzedaż (VAT należny/pobrany)",
};

const VatRegister = ({ searchText, setSubtitle }: Props) => {
  const [from, setFrom] = useState(`${CURRENT_YEAR}-01-01`);
  const [to, setTo] = useState(todayISO());
  const [type, setType] = useState<"" | "Purchase" | "Sale">("");

  const { data: rows = [], isLoading } = useVatRegister(from, to, type || undefined);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.documentNumber ?? "").toLowerCase().includes(q) ||
        (r.contractorName ?? "").toLowerCase().includes(q),
    );
  }, [rows, searchText]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => ({ net: acc.net + r.net, vat: acc.vat + r.vat, gross: acc.gross + r.gross }),
        { net: 0, vat: 0, gross: 0 },
      ),
    [filtered],
  );

  // Podział VAT: należny/pobrany (sprzedaż) vs naliczony (zakup) + saldo do US.
  const split = useMemo(() => {
    let nalezny = 0;
    let naliczony = 0;
    for (const r of filtered) {
      if (r.type === "Sale") nalezny += r.vat;
      else naliczony += r.vat;
    }
    return { nalezny, naliczony, saldo: nalezny - naliczony };
  }, [filtered]);

  useEffect(() => {
    setSubtitle(`Rejestr VAT · ${plDate(from)} – ${plDate(to)}`);
  }, [from, to, setSubtitle]);

  const cell: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid #eee" };
  const num: React.CSSProperties = { ...cell, textAlign: "right", whiteSpace: "nowrap" };
  const head: React.CSSProperties = {
    padding: "8px 10px",
    borderBottom: "2px solid #e5e0ec",
    color: "#79767a",
    fontWeight: 600,
    fontSize: 12,
    textAlign: "left",
    position: "sticky",
    top: 0,
    background: "#fdf8fd",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fdf8fd" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 16px", flexWrap: "wrap" }}>
        <TextField
          label="Okres od"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          label="Okres do"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Rejestr</InputLabel>
          <Select label="Rejestr" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <MenuItem value="">Wszystkie</MenuItem>
            <MenuItem value="Purchase">Zakup (VAT naliczony)</MenuItem>
            <MenuItem value="Sale">Sprzedaż (VAT należny/pobrany)</MenuItem>
          </Select>
        </FormControl>
      </div>

      {/* Podsumowanie: VAT należny/pobrany, naliczony i saldo do rozliczenia z US. */}
      <div style={{ display: "flex", gap: 12, padding: "0 16px 12px", flexWrap: "wrap" }}>
        {[
          { label: "VAT należny (pobrany) — sprzedaż", value: split.nalezny, color: "#1565c0" },
          { label: "VAT naliczony — zakup", value: split.naliczony, color: "#2e7d32" },
          {
            label: split.saldo >= 0 ? "Do zapłaty (należny − naliczony)" : "Nadwyżka do zwrotu / przeniesienia",
            value: Math.abs(split.saldo),
            color: split.saldo >= 0 ? "#c62828" : "#00695c",
          },
        ].map((t) => (
          <div
            key={t.label}
            style={{
              flex: "1 1 200px",
              minWidth: 200,
              border: `1px solid ${t.color}33`,
              background: `${t.color}0d`,
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            <div style={{ fontSize: 11, color: "#79767a", fontWeight: 600 }}>{t.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.color, marginTop: 2 }}>
              {fmt(t.value)} zł
            </div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={head}>Data wpisu</th>
              <th style={head}>Nr dokumentu</th>
              <th style={head}>Kontrahent</th>
              <th style={head}>Rejestr</th>
              <th style={{ ...head, textAlign: "right" }}>Netto</th>
              <th style={{ ...head, textAlign: "right" }}>VAT</th>
              <th style={{ ...head, textAlign: "right" }}>Brutto</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td style={cell} colSpan={7}>Ładowanie…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td style={{ ...cell, color: "#aaa", fontStyle: "italic" }} colSpan={7}>Brak wpisów VAT w okresie.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.postingId}>
                  <td style={cell}>{plDate(r.entryDate)}</td>
                  <td style={cell}>{r.documentNumber || "—"}</td>
                  <td style={cell}>{r.contractorName || "—"}</td>
                  <td style={cell}>{TYPE_LABEL[r.type] ?? r.type}</td>
                  <td style={num}>{fmt(r.net)}</td>
                  <td style={num}>{fmt(r.vat)}</td>
                  <td style={num}>{fmt(r.gross)}</td>
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td style={cell} colSpan={4}>RAZEM</td>
                <td style={num}>{fmt(totals.net)}</td>
                <td style={num}>{fmt(totals.vat)}</td>
                <td style={num}>{fmt(totals.gross)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default VatRegister;
